/**
 * API服务层 - 处理所有后端API调用
 * 基于原有的script_ja.js中的API调用逻辑
 */

// API配置
export const API_CONFIG = {
  MAIN_API_BASE: process.env.REACT_APP_MAIN_API_BASE || 'http://localhost:8888',      // 主API服务
  STREAMING_API_BASE: process.env.REACT_APP_STREAMING_API_BASE || 'http://localhost:8889', // 流式对话服务
  ENHANCED_ANALYSIS_BASE: process.env.REACT_APP_ENHANCED_ANALYSIS_BASE || 'http://localhost:8887', // 增强分析服务（三阶段分析）
  CURRENT_API_BASE: process.env.REACT_APP_CURRENT_API_BASE || 'http://localhost:8080',   // 当前前端API
  PAYMENT_API_BASE: process.env.REACT_APP_PAYMENT_API_BASE || 'http://localhost:9002',   // 支付服务
};

// 类型定义
export interface ConversationState {
  conversationId: string | null;
  state: 'initial' | 'clarifying' | 'confirmed' | 'analyzing' | 'completed';
  caseInfo: Record<string, any>;
  completenessScore: number;
  rawCaseInfo?: Record<string, any>;
}

export interface StreamingData {
  type: 'status' | 'content' | 'complete' | 'error';
  data: any;
}

export interface ClarificationResponse {
  needs_clarification: boolean;
  clarification_question?: string;
  case_info?: Record<string, any>;
  updated_case_info?: Record<string, any>;
  analysis_capability?: string;
  completeness_score?: number;
  next_action?: string;
  state?: string;
  conversation_id?: string;
}

export interface MultimodalUploadResponse {
  success: boolean;
  file_info: {
    filename: string;
    extraction_method: string;
    extracted_content: string;
    processing_time: number;
  };
  clarification: ClarificationResponse;
}

/**
 * 流式对话API调用
 */
export class StreamingConversationAPI {
  static async startConversation(
    query: string,
    conversationId: string | null = null,
    language: string = 'ja',
    conversationHistory: Array<{role: string, content: string}> = []
  ): Promise<Response> {
    const url = `${API_CONFIG.STREAMING_API_BASE}/api/streaming/conversation`;
    
    // 由于EventSource不支持POST，我们需要用fetch实现
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        conversation_id: conversationId,
        language,
        conversation_history: conversationHistory  // 传递完整对话历史
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response;
  }

  /**
   * 处理流式响应数据
   */
  static async processStreamingResponse(
    response: Response,
    onStatus: (data: any) => void,
    onContent: (data: any) => void,
    onComplete: (data: any) => void,
    onError: (error: string) => void
  ): Promise<void> {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim()) {
              try {
                const streamData = JSON.parse(data);
                
                switch (streamData.type) {
                  case 'status':
                    onStatus(streamData.data);
                    break;
                  case 'content':
                    onContent(streamData.data);
                    break;
                  case 'complete':
                    onComplete(streamData.data);
                    return;
                  case 'error':
                    onError(streamData.data.message);
                    return;
                }
              } catch (parseError) {
                // 忽略解析错误
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

/**
 * 法律分析API
 */
export class LegalAnalysisAPI {
  /**
   * 流式法律分析
   */
  static async streamingAnalyze(query: string, language: string = 'zh'): Promise<Response> {
    const response = await fetch(`${API_CONFIG.MAIN_API_BASE}/api/streaming/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        query,
        language
      })
    });

    if (!response.ok) {
      throw new Error(`分析请求失败: ${response.status}`);
    }

    return response;
  }

  /**
   * 传统法律分析（备用）
   */
  static async traditionalAnalyze(query: string): Promise<any> {
    const response = await fetch('/api/analyze', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      throw new Error(`分析请求失败: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '分析失败');
    }

    return data.data;
  }

  /**
   * 快速案例搜索
   */
  static async quickSearch(query: string, topK: number = 5): Promise<any> {
    const response = await fetch('/api/quick-search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, top_k: topK })
    });

    if (!response.ok) {
      throw new Error(`搜索请求失败: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '搜索失败');
    }

    return data.data;
  }
}

/**
 * 多模态处理API
 */
export class MultimodalAPI {
  /**
   * 文件上传+案件澄清集成
   */
  static async uploadAndClarify(
    file: File,
    userQuery: string = '',
    userId: string = 'anonymous',
    sessionId: string | null = null,
    language: string = 'ja'
  ): Promise<MultimodalUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_query', userQuery);
    formData.append('user_id', userId);
    formData.append('language', language);
    if (sessionId) {
      formData.append('session_id', sessionId);
    }

    const response = await fetch(`${API_CONFIG.MAIN_API_BASE}/api/multimodal/upload-and-clarify`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`文件上传失败: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '文件处理失败');
    }

    return data;
  }

  /**
   * 简单内容提取（备用）
   */
  static async extractContent(file: File, userId: string = 'anonymous'): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);

    const response = await fetch(`${API_CONFIG.MAIN_API_BASE}/api/simple-multimodal/extract-content`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`内容提取失败: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '内容提取失败');
    }

    return data;
  }
}

/**
 * 传统对话API（备用）
 */
export class TraditionalConversationAPI {
  /**
   * 开始新对话
   */
  static async startConversation(query: string): Promise<any> {
    const response = await fetch('/api/conversation/start', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      throw new Error(`对话开始失败: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '对话开始失败');
    }

    return data.data;
  }

  /**
   * 继续对话
   */
  static async continueConversation(conversationId: string, userResponse: string): Promise<any> {
    const response = await fetch('/api/conversation/continue', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        user_response: userResponse
      })
    });

    if (!response.ok) {
      throw new Error(`对话继续失败: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '对话继续失败');
    }

    return data.data;
  }

  /**
   * 获取对话状态
   */
  static async getConversationStatus(conversationId: string): Promise<any> {
    const response = await fetch(`/api/conversation/status/${conversationId}`);

    if (!response.ok) {
      throw new Error(`获取对话状态失败: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '获取对话状态失败');
    }

    return data.data;
  }
}

/**
 * 系统状态API
 */
export class SystemAPI {
  /**
   * 获取系统状态
   */
  static async getStatus(): Promise<any> {
    const response = await fetch('/api/status');

    if (!response.ok) {
      throw new Error(`获取系统状态失败: ${response.status}`);
    }

    const data = await response.json();
    return data;
  }

  /**
   * 健康检查
   */
  static async healthCheck(): Promise<any> {
    const response = await fetch('/api/health');

    if (!response.ok) {
      throw new Error(`健康检查失败: ${response.status}`);
    }

    const data = await response.json();
    return data;
  }
}

/**
 * 案件信息处理工具
 */
export class CaseInfoProcessor {
  /**
   * 字段映射配置（与原始JS保持一致）
   */
  static readonly FIELD_MAPPINGS = [
    { keys: ['client_claim', 'user_expectations', 'plaintiff_demands', 'plaintiff_demand', 'party_demands'], label: '依頼者の要求', icon: '📝' },
    { keys: ['case_details', 'timeline', 'case_timeline'], label: 'タイムライン', icon: '⏰' },
    { keys: ['opposing_party_actions', 'dispute_points', 'defendant_actions', 'defendant_behavior', 'opposing_party_behavior'], label: '相手方の行動', icon: '⚔️' },
    { keys: ['client_situation', 'key_facts', 'client_status', 'client_condition', 'plaintiff_conditions'], label: '依頼者の状況', icon: '👤' },
    { keys: ['evidence', 'evidence_materials', 'evidence_material'], label: '証拠資料', icon: '📄' },
    { keys: ['case_history'], label: '事案の経緯', icon: '📚' },
    { keys: ['location'], label: '場所', icon: '📍' },
    { keys: ['parties'], label: '当事者', icon: '👥' },
    { keys: ['legal_issues'], label: '法的問題', icon: '📖' },
    { keys: ['damages'], label: '損害状況', icon: '💰' },
    { keys: ['urgency'], label: '緊急度', icon: '🚨' }
  ];

  /**
   * 生成中日双语的案件要点总结 
   */
  static generateCaseInfoSummary(caseInfo: Record<string, any>, completenessScore: number, analysisCapability: string, language: string = 'zh'): string {
    // 根据语言生成标题和模板
    const isJapanese = language === 'ja';
    
    let summary = isJapanese 
        ? 'ご提供いただいた情報をありがとうございます！要点を収集いたしました：\n\n'
        : '感谢您提供的信息！以下是收集到的案件要点：\n\n';

    // 用标准化的字段提取信息
    const clientClaim = caseInfo.client_claim || caseInfo.user_expectations?.[0] || '';
    const caseDetails = caseInfo.case_details || caseInfo.timeline || '';
    const opposingPartyActions = caseInfo.opposing_party_actions || caseInfo.dispute_points?.[0] || '';
    const clientSituation = caseInfo.client_situation || caseInfo.key_facts?.[0] || '';
    const evidence = caseInfo.evidence || (Array.isArray(caseInfo.evidence) ? caseInfo.evidence.join('、') : '');

    if (clientClaim) {
        const label = isJapanese ? '📝 依頼者の意向に関する情報：' : '📝 当事人诉求：';
        summary += `${label}${clientClaim}\n\n`;
    }

    if (caseDetails) {
        const label = isJapanese ? '⏰ 事実関係の流れ：' : '⏰ 事件经过：';
        summary += `${label}${caseDetails}\n\n`;
    }

    if (opposingPartyActions) {
        const label = isJapanese ? '⚔️ 相手方の行動：' : '⚔️ 对方行为：';
        summary += `${label}${opposingPartyActions}\n\n`;
    }

    if (clientSituation) {
        const label = isJapanese ? '👤 依頼者の状況：' : '👤 当事人情况：';
        summary += `${label}${clientSituation}\n\n`;
    }

    if (evidence) {
        const label = isJapanese ? '📄 関連資料：' : '📄 证据材料：';
        summary += `${label}${evidence}\n\n`;
    }

    // 添加系统收集信息的说明
    const systemNote = isJapanese
        ? '📋 依頼内容を収集いたしました。整理した関連情報をご確認ください。内容に誤りがなければ、参考となる情報の提示を進めさせていただきます。\n\n'
        : '📋 系统已收集您的案件信息，分析准备就绪。\n\n';
    summary += systemNote;

    // 添加确认信息
    // const confirmationText = isJapanese
    //     ? '情報をご確認ください。間違いがなければ、詳細な法律分析を開始いたします。'
    //     : '请确认信息无误。如无问题，将开始详细的法律分析。';
    // summary += confirmationText;

    return summary;
  }

  /**
   * 生成案件分析查询
   */
  static generateAnalysisQuery(caseInfo: Record<string, any>): string {
    let query = `以下の法律事件を分析してください：\n\n`;
    
    let foundFields = 0;
    this.FIELD_MAPPINGS.forEach(mapping => {
      const foundKey = mapping.keys.find(key => caseInfo[key]);
      
      if (foundKey && caseInfo[foundKey]) {
        const value = caseInfo[foundKey];
        const formattedValue = this.formatObjectValue(value);
        if (formattedValue && formattedValue.trim()) {
          query += `${mapping.label}：${formattedValue}\n`;
          foundFields++;
        }
      }
    });
    
    if (foundFields === 0) {
      query += `案件情報：${JSON.stringify(caseInfo, null, 2)}\n`;
    }
    
    query += `\n詳細な法律分析とアドバイスを提供してください。`;
    
    return query;
  }

  /**
   * 通用对象格式化方法（与原始JS保持一致）
   */
  static formatObjectValue(value: any): string {
    if (!value) return '';
    
    if (typeof value === 'string') {
      return value.trim();
    }
    
    if (Array.isArray(value)) {
      return value.map(item => this.formatObjectValue(item))
                   .filter(item => item.trim())
                   .join(', ');
    }
    
    if (typeof value === 'object' && value !== null) {
      if (value.fact) return value.fact;
      if (value.type) return value.type + (value.description ? `：${value.description}` : '');
      if (value.name) return value.name + (value.role ? ` (${value.role})` : '') + (value.description ? ` - ${value.description}` : '');
      if (value.types && Array.isArray(value.types)) return value.types.join(', ');
      
      const entries = Object.entries(value)
        .filter(([key, val]: [string, unknown]) => val && val.toString().trim())
        .map(([key, val]: [string, unknown]) => {
          if (key === 'types' && Array.isArray(val)) {
            return val.join(', ');
          }
          return val!.toString().trim();
        })
        .filter(val => val);
      
      if (entries.length > 0) {
        return entries.join(', ');
      }
    }
    
    return value.toString().trim();
  }
}

/**
 * 三阶段分析API - 使用增强分析服务（8887端口）
 */
export class ThreePhaseAnalysisAPI {
  /**
   * 启动三阶段流式分析
   */
  static async startThreePhaseAnalysis(
    query: string, 
    language: string = 'zh'
  ): Promise<Response> {
    const response = await fetch(`${API_CONFIG.ENHANCED_ANALYSIS_BASE}/api/analyze/three-phase/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        language
      })
    });

    if (!response.ok) {
      throw new Error(`三阶段分析请求失败: ${response.status} - ${response.statusText}`);
    }

    return response;
  }

  /**
   * 启动第三阶段分析（基于用户选择的策略）
   */
  static async startPhase3Analysis(
    userQuery: string,
    matchedCases: any[],
    phase1Result: string,
    selectedStrategy: string,
    language: string = 'zh'
  ): Promise<Response> {
    const response = await fetch(`${API_CONFIG.ENHANCED_ANALYSIS_BASE}/api/analyze/phase3/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_query: userQuery,
        matched_cases: matchedCases,
        phase1_result: phase1Result,
        selected_strategy: selectedStrategy,
        language
      })
    });

    if (!response.ok) {
      throw new Error(`第三阶段分析请求失败: ${response.status} - ${response.statusText}`);
    }

    return response;
  }

  /**
   * 提交策略选择
   */
  static async submitStrategySelection(
    sessionId: string,
    strategyId: string
  ): Promise<any> {
    const response = await fetch(`${API_CONFIG.ENHANCED_ANALYSIS_BASE}/api/analyze/strategy-selection`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id: sessionId,
        strategy_id: strategyId
      })
    });

    if (!response.ok) {
      throw new Error(`策略选择提交失败: ${response.status} - ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 处理三阶段流式响应数据
   */
  static async processThreePhaseStreamingResponse(
    response: Response,
    onPhaseStart: (phase: number, phaseName: string) => void,
    onContent: (phase: number, content: string) => void,
    onPhaseComplete: (phase: number, result: any) => void,
    onAllComplete: (results: any) => void,
    onError: (error: string) => void,
    onStrategySelection?: (strategies: any[], message: string, canModify: boolean, showDocumentSupport?: boolean) => void,
    onStrategyModificationPrompt?: (message: string, currentStrategy: string) => void
  ): Promise<void> {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim() === '') continue;
          
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            
            if (data === '[DONE]') {
              onAllComplete({});
              return;
            }

            try {
              const parsed = JSON.parse(data);
              
              switch (parsed.type) {
                case 'phase_start':
                  onPhaseStart(parsed.data.phase, parsed.data.title || '');
                  break;
                  
                case 'content':
                  onContent(parsed.data.phase || 1, parsed.data.content || '');
                  break;
                  
                case 'phase_complete':
                  onPhaseComplete(parsed.data.phase || 1, parsed.data);
                  break;
                  
                case 'strategy_selection':
                  if (onStrategySelection) {
                    onStrategySelection(
                      parsed.data.strategies || [],
                      parsed.data.message || '',
                      parsed.data.can_modify || false,
                      parsed.data.show_document_support || false
                    );
                  }
                  break;
                  
                case 'strategy_modification_prompt':
                  if (onStrategyModificationPrompt) {
                    onStrategyModificationPrompt(
                      parsed.data.message || '',
                      parsed.data.current_strategy || ''
                    );
                  }
                  break;
                  
                case 'analysis_complete':
                  onAllComplete(parsed.data);
                  return;
                  
                case 'end':
                  // 处理流结束信号
                  onAllComplete(parsed.data);
                  return;
                  
                case 'error':
                  onError(parsed.data.message || '未知错误');
                  break;
              }
            } catch (parseError) {
              console.warn('解析流式数据失败:', parseError, '原始数据:', data);
            }
          }
        }
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : '流式处理失败');
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * 获取案例匹配分析
   */
  static async getCaseMatchingAnalysis(
    query: string,
    topK: number = 5,
    includeChatGPTAnalysis: boolean = true,
    analysisType: string = 'comprehensive'
  ): Promise<any> {
    const response = await fetch(`${API_CONFIG.ENHANCED_ANALYSIS_BASE}/legal-case-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        top_k: topK,
        include_chatgpt_analysis: includeChatGPTAnalysis,
        analysis_type: analysisType
      })
    });

    if (!response.ok) {
      throw new Error(`案例匹配分析失败: ${response.status}`);
    }

    return await response.json();
  }
}

export default {
  StreamingConversationAPI,
  LegalAnalysisAPI,
  MultimodalAPI,
  TraditionalConversationAPI,
  SystemAPI,
  CaseInfoProcessor,
  ThreePhaseAnalysisAPI
}; 